import { useQuery } from '@tanstack/react-query';
import { TypeBlogPostSkeleton, stringToSlug } from '@wren/shared';
import { Entry } from 'contentful';
import first from 'lodash/first';
import React, { useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, Redirect } from 'react-router-dom';
import { Headings, Paragraph } from '../../components/UI/TextStyles';
import { NEWEST_FINANCIAL_REPORT_URL } from '../../config/constants';
import {
  articleStyles,
  container,
  heroImage,
  postContent,
  postContentContainer,
  postHeader,
} from '../../style/article';
import { colors, fonts, spacing } from '../../style/theme';
import { scale } from '../../util/scale';
import RichText from '../contentful/RichText';
import BackLink from '../frontfacing/BackLink';
import Footer from '../frontfacing/Footer';
import { PostMetadata, PostShareButtons } from '../layout/Article';
import {
  ContactCallout,
  teamMembers,
} from '../projects/ProjectPageSections/ContactCallout';
import { ProjectUpdates } from '../projects/ProjectUpdates';
import SkeletonLine from '../UI/SkeletonLine';
import DefaultSignature from './DefaultSignature';
import FundraiserCallout from './FundraiserCallout';
import JustTheGist from './JustTheGist';
import OutdatedFinancialReportCallout from './OutdatedFinancialReportCallout';

const BLOG_API = `${process.env.REACT_APP_API_URL || '/api'}/blog`;

type BlogPostEntry = Entry<
  TypeBlogPostSkeleton,
  'WITHOUT_UNRESOLVABLE_LINKS',
  string
>;

export default function ContentfulBlogPost({
  slug,
}: {
  slug: string /* aka link */;
}) {
  const pageResult = useQuery<{ items: BlogPostEntry[] }>({
    queryKey: ['blogPost', slug],
    queryFn: async () => {
      const res = await fetch(`${BLOG_API}/raw/posts/${slug}`);
      if (!res.ok) throw new Error('Failed to fetch blog post');
      return res.json();
    },
    staleTime: 300000, // 5 minutes
  });

  const wrapperRef = useRef(null);

  if (!pageResult.isFetched) {
    return <SkeletonLine />;
  }

  const page = first(pageResult.data?.items);

  if (!page) {
    return <Redirect to="/blog" />;
  }

  const formatDate = (date: string | number | Date) => {
    const dt = new Date(date);
    const day = dt.getDate();
    let suffix = 'th';
    if (day % 10 === 1 && day % 100 !== 11) {
      suffix = 'st';
    } else if (day % 10 === 2 && day % 100 !== 12) {
      suffix = 'nd';
    } else if (day % 10 === 3 && day % 100 !== 13) {
      suffix = 'rd';
    }
    const month = dt.toLocaleString('default', { month: 'long' });
    const year = dt.getFullYear();
    return `${month} ${day}${suffix}, ${year}`;
  };

  type RichTextNode = {
    value?: string;
    marks?: Array<{ type: string }>;
  };
  type RichTextBlock = { content?: RichTextNode[] };
  type RichTextDoc = { content?: RichTextBlock[] };

  const richTextToInlineElements = (
    doc: unknown
  ): React.ReactNode | undefined => {
    if (!doc || typeof doc !== 'object') return undefined;
    const items = (doc as RichTextDoc).content;
    if (!Array.isArray(items)) return undefined;
    const elements: React.ReactNode[] = [];
    let key = 0;
    for (const item of items) {
      const contents = item?.content;
      if (Array.isArray(contents)) {
        for (const c of contents) {
          if (!c?.value) continue;
          let node: React.ReactNode = c.value;
          if (c.marks) {
            for (const mark of c.marks) {
              if (mark.type === 'italic') node = <em>{node}</em>;
              else if (mark.type === 'bold') node = <b>{node}</b>;
              else if (mark.type === 'underline') node = <u>{node}</u>;
            }
          }
          elements.push(<React.Fragment key={key++}>{node}</React.Fragment>);
        }
      }
    }
    return elements.length ? elements : undefined;
  };

  const {
    title,
    publishDate,
    author,
    authors = [],
    featureImage,
    justTheGist,
    content,
    signature,
    tags,
  } = page.fields;

  const fundraiserTag = tags?.find((tag) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(tag)
  );
  const isOldFinancialReport =
    tags?.find((tag) => /financial/i.test(tag)) &&
    slug !== NEWEST_FINANCIAL_REPORT_URL.split('blog/').pop();
  const hasWrenTravelTag = tags?.some((tag) => /wren travel/i.test(tag));

  const getPointOfContact = (): keyof typeof teamMembers | undefined => {
    if (hasWrenTravelTag) return 'Christina';

    return undefined;
  };

  return (
    <div css={{ backgroundColor: 'white', overflowX: 'hidden' }}>
      <Helmet>
        <title>Wren | {title}</title>
        <meta property="og:title" content={`Wren | ${title}`} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://www.wren.co/blog/${slug}`} />
        <meta
          property="og:image"
          content={
            featureImage?.fields.file?.url || 'https://i.imgur.com/7tarhAU.jpg'
          }
        />
        <meta property="og:description" content={`Wren | ${title || 'Blog'}`} />
      </Helmet>
      <div ref={wrapperRef} css={[container, { display: 'flow-root' }]}>
        <div css={postContentContainer}>
          <img
            css={heroImage}
            src={featureImage?.fields.file?.url}
            alt={title}
          />
          <BackLink destination="/blog" />
          <div css={postContent}>
            <header css={postHeader}>
              <div
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 16,
                  marginBottom: 8,
                }}
              >
                <div css={{ opacity: 0.4 }}>
                  {publishDate && (
                    <Paragraph>{formatDate(publishDate)}</Paragraph>
                  )}
                </div>
                <div css={{ width: 132, flexShrink: 0 }}>
                  <PostShareButtons post={{ title, slug }} sharePath="blog" />
                </div>
              </div>
              <Headings.Large
                style={{ fontSize: 40, fontWeight: 700, lineHeight: 1.1 }}
                font="serif"
              >
                {title}
              </Headings.Large>
              <PostMetadata
                sharePath={'blog'}
                hideDateAndReadTime
                authorLinkPrefix="/blog/author"
                hideSocialIcons
                post={{
                  authors: [author, ...authors].filter(Boolean).map((a) => ({
                    name: a?.fields.name,
                    profile_image: a?.fields.logotype?.fields.file?.url,
                    location: richTextToInlineElements(a?.fields.description),
                  })),
                  group: author?.fields.name ? (
                    <>
                      {author.fields.name}
                      {author?.fields.description && (
                        <>
                          ,{' '}
                          {richTextToInlineElements(author?.fields.description)}
                        </>
                      )}
                    </>
                  ) : undefined,
                  group_title:
                    [...authors].filter(Boolean).length > 0 ? (
                      <>
                        {[...authors].filter(Boolean).map((a, i) => (
                          <React.Fragment key={a?.sys.id ?? i}>
                            {i > 0 && ', '}
                            {a?.fields.name}
                            {a?.fields.description && (
                              <>
                                {' '}
                                (
                                {richTextToInlineElements(
                                  a?.fields.description
                                )}
                                )
                              </>
                            )}
                          </React.Fragment>
                        ))}
                      </>
                    ) : undefined,
                  slug,
                }}
              />
            </header>
            {fundraiserTag && (
              <FundraiserCallout fundraiserTag={fundraiserTag} />
            )}
            {isOldFinancialReport && <OutdatedFinancialReportCallout />}
            {justTheGist && <JustTheGist document={justTheGist} />}
            {content && <RichText css={[articleStyles]} document={content} />}
            {signature ? (
              <RichText
                css={[articleStyles, { marginTop: spacing.large }]}
                document={signature}
              />
            ) : (
              <DefaultSignature />
            )}
            <div css={{ paddingTop: spacing.large }}>
              <ContactCallout title={title} member={getPointOfContact()} />
            </div>
          </div>
          <div
            css={{
              display: 'flex',
              gap: 8,
              flexWrap: 'wrap',
              marginTop: 24,
              justifyContent: 'center',
            }}
          >
            {[...(tags ?? [])]
              .sort((a, b) => a.length - b.length)
              .map((tag) => (
                <Link
                  key={tag}
                  to={`/blog/tag/${stringToSlug(tag)}`}
                  css={scale({
                    backgroundColor: colors.gray9,
                    color: colors.textPrimary,
                    padding: '3px 10px',
                    borderRadius: 24,
                    fontSize: 12,
                    fontFamily: fonts.Sans,
                    display: ['none', 'inline-block'],
                    margin: spacing.xxSmall,
                    marginLeft: 0,
                    fontWeight: 600,
                    width: 'fit-content',
                    textDecoration: 'none',
                  })}
                >
                  {tag}
                </Link>
              ))}
          </div>
          <div css={{ marginTop: spacing.xxLarge }}>
            <Headings.Large>You might also like </Headings.Large>
            <ProjectUpdates
              verticalOrHorizontal="horizontal"
              tags={tags ?? []}
              ignoreSlug={slug}
            />
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
